import React from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/heatmap';

import { formatSimpleDuration } from '@/datetime/dateTime.utilities';
import { getXYRegion, setScatterPlotExtremes } from '@/utilities/chartHelper.utilities';
import {
  BIN_SIZE_MULTIPLIER,
  SeeqDensityPlotPoint,
  TIME_COLOR_STOPS_WITH_ZERO,
} from '@/scatterPlot/densityPlot.selectors';
import { XyPlotChartIdsInterface } from '@/scatterPlot/XyPlotChartWrapper.organism';
import { formatNumber } from '@/utilities/numberHelper.utilities';
import { useTranslation } from 'react-i18next';
import { XYPlotRegion } from '@/scatterPlot/scatterPlot.constants';
import { CHART_FONT_STYLE } from '@/chart/chart.constants';
import {
  DEFAULT_AXIS_LABEL_COLOR,
  DEFAULT_AXIS_LABEL_COLOR_DARK,
  DEFAULT_AXIS_LINE_COLOR,
} from '@/trend/trendViewer/trendViewer.constants';

export interface DensityPlotChartProps {
  viewRegion: XYPlotRegion;
  setSelectedRegion: (region: XYPlotRegion) => void;
  setChart: (chart: Highcharts.Chart) => void;
  setOnMouseOverRef: (chartData: XyPlotChartIdsInterface) => void;
  onMouseOverRef: any;
  darkMode?: boolean;
}

export interface DensityPlotChartExternalProps
  extends Omit<DensityPlotChartProps, 'setChart' | 'onMouseOverRef' | 'setOnMouseOverRef'> {}

export const DensityPlotChart: React.FunctionComponent<DensityPlotChartProps> = ({
  viewRegion,
  setSelectedRegion,
  setChart,
  setOnMouseOverRef,
  onMouseOverRef,
  darkMode,
}) => {
  const { t } = useTranslation();
  const durationLabel = t('DENSITY_PLOT.DURATION');

  const formatTooltip = (point: SeeqDensityPlotPoint) => {
    const seriesUserOptions: any = point.series.userOptions;

    const xRange = seriesUserOptions.colsize / BIN_SIZE_MULTIPLIER;
    const yRange = seriesUserOptions.rowsize / BIN_SIZE_MULTIPLIER;
    const xSignalName = seriesUserOptions.xSignalName;
    const ySignalName = seriesUserOptions.ySignalName;

    const xMinFormatted = formatNumber(point.x - xRange / 2, {
      format: seriesUserOptions.xNumberFormat,
    });
    const xMaxFormatted = formatNumber(point.x + xRange / 2, {
      format: seriesUserOptions.xNumberFormat,
    });
    const yMinFormatted = formatNumber((point.y as number) - yRange / 2, {
      format: seriesUserOptions.yNumberFormat,
    });
    const yMaxFormatted = formatNumber((point.y as number) + yRange / 2, {
      format: seriesUserOptions.yNumberFormat,
    });
    const tooltipFormatted =
      `${xSignalName}: <strong>${xMinFormatted} - ${xMaxFormatted}</strong><br/>` +
      `${ySignalName}: <strong>${yMinFormatted} - ${yMaxFormatted}</strong><br/>`;
    const timeFormatted = formatSimpleDuration(moment.duration(point.value));
    return `${tooltipFormatted}${durationLabel}: <strong>${timeFormatted}</strong>`;
  };

  /**
   * Callback method to be passed into the HighchartsReact object so we have access to the Highcharts Chart
   *
   * @param highchartsChart - the chart object from Highcharts
   */
  const afterChartCreated = (highchartsChart: Highcharts.Chart) => {
    setChart(highchartsChart);
    setOnMouseOverRef({ chart: highchartsChart });
  };

  const getInitialChartConfig = (): Highcharts.Options => ({
    chart: {
      animation: false,
      type: 'heatmap',
      zooming: { type: 'xy' },
      alignTicks: false,
      style: CHART_FONT_STYLE,
      events: {
        selection(e) {
          setSelectedRegion(getXYRegion(e.xAxis[0], e.yAxis));
          return false;
        },
        load() {
          setScatterPlotExtremes(this.xAxis[0], this.yAxis, viewRegion);
        },
      },
    },
    tooltip: {
      animation: false,
      hideDelay: 100,
      headerFormat: '',
      formatter() {
        const point = this as SeeqDensityPlotPoint;
        // turn off tooltips for null or 0 values
        if (_.isNil(point.value) || point.value === 0) {
          return false;
          // custom tooltip for the real values
        } else {
          return formatTooltip(point);
        }
      },
    },
    plotOptions: {
      series: {
        turboThreshold: 0,
        animation: false,
        states: {
          hover: {
            lineWidthPlus: 0,
          },
          inactive: {
            enabled: false,
          },
        },
        borderWidth: 0,
      },
      heatmap: {
        point: {
          events: { mouseOver: onMouseOverRef.current },
        },
        pointPadding: 0,
        minPadding: 0,
        maxPadding: 0,
        crisp: false,
      },
    },
    colorAxis: {
      startOnTick: false,
      endOnTick: false,
      type: 'linear',
      stops: TIME_COLOR_STOPS_WITH_ZERO,
      labels: {
        formatter() {
          return formatSimpleDuration(moment.duration(this.value));
        },
        rotation: 15,
      },
    },
    credits: { enabled: false },
    title: { text: undefined },
    xAxis: {
      title: {
        text: '',
      },
      labels: {
        y: 20,
        style: {
          color: darkMode ? DEFAULT_AXIS_LABEL_COLOR_DARK : DEFAULT_AXIS_LABEL_COLOR,
        },
      },
      lineWidth: 1,
      tickWidth: 1,
      maxPadding: 0,
      minPadding: 0,
      lineColor: DEFAULT_AXIS_LINE_COLOR,
      tickColor: DEFAULT_AXIS_LINE_COLOR,
    },
    yAxis: {
      title: {
        text: '',
      },
      // The default tick behavior (for the y-axis only) causes the axis to snap extremes to the nearest tick every
      // time you change it, which causes very weird behavior when panning and zooming on it
      startOnTick: false,
      endOnTick: false,
      lineWidth: 1,
      tickWidth: 1,
      gridLineWidth: 0,
      maxPadding: 0,
      minPadding: 0,
      lineColor: DEFAULT_AXIS_LINE_COLOR,
      tickColor: DEFAULT_AXIS_LINE_COLOR,
      labels: {
        style: {
          color: darkMode ? DEFAULT_AXIS_LABEL_COLOR_DARK : DEFAULT_AXIS_LABEL_COLOR,
        },
      },
      zIndex: 90,
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      width: 100,
    },
  });

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={getInitialChartConfig()}
      // Don't let the chart auto-update because we have to handle updates in a custom way (see updateChart())
      allowChartUpdate={false}
      callback={afterChartCreated}
      containerProps={{ className: 'flexFill' }}
    />
  );
};
